import { create } from "zustand";
import {
  loadGameState,
  loadUserProfile,
  saveGameState,
} from "../services/storageService";
import { ELEMENT_DEFS } from "../components/elementDefinitions";
import { syncGameState } from "../services/firebaseService";

export interface ElementDef {
  id: string;
  name: string;
  tier: number;
  recipe: string[];
  color?: string;
  shape?: "circle" | "square" | "triangle";
  animation?: string;
}

// Base elements (tier 1 only)
const baseElements: Record<string, ElementDef> = {};
Object.values(ELEMENT_DEFS).forEach((element) => {
  if (element.tier === 1 && element.recipe.length === 0) {
    baseElements[element.id] = element;
  }
});

// Create recipe maps for different numbers of ingredients.
// Guard against the two bug classes that broke player progression (§3):
//   1. Recipe collisions — two elements sharing the same ingredient key, which
//      silently overwrote each other (making elements undiscoverable / "wrong
//      answer"). We throw in dev/test so CI catches any regression, and in
//      production keep the FIRST definition (deterministic) instead of crashing.
//   2. Recipes that reference a non-existent ingredient id.
const recipeMap = new Map<string, string>();
const recipeMap3 = new Map<string, string>();
const recipeMap4 = new Map<string, string>();

const isProd = process.env.NODE_ENV === "production";
const reportRecipeError = (msg: string) => {
  if (isProd) console.error(`[recipe engine] ${msg}`);
  else throw new Error(`[recipe engine] ${msg}`);
};

Object.values(ELEMENT_DEFS).forEach((element) => {
  if (!element.recipe || element.recipe.length === 0) return;

  // 2. every ingredient must resolve to a real element
  for (const ingredient of element.recipe) {
    if (!ELEMENT_DEFS[ingredient]) {
      reportRecipeError(
        `"${element.id}" references missing ingredient "${ingredient}"`
      );
    }
  }

  const key = [...element.recipe].sort().join(",");
  const target =
    element.recipe.length === 2
      ? recipeMap
      : element.recipe.length === 3
      ? recipeMap3
      : element.recipe.length === 4
      ? recipeMap4
      : null;
  if (!target) return;

  // 1. unique key per recipe
  const existing = target.get(key);
  if (existing && existing !== element.id) {
    reportRecipeError(
      `collision on "${key}": ${existing} vs ${element.id}`
    );
    return; // keep the first definition in production
  }
  target.set(key, element.id);
});

interface ElementState {
  all: Record<string, ElementDef>;
  unlocked: Set<string>;
  selected: string[];
  history: string[][];
  unlock: (id: string) => void;
  undoLastAction: () => void;
  select: (id: string, zoneIndex: number) => void;
  clearSelection: () => void;
  discoverRecipe: (ingredients: string[]) => string | null;
  /** Non-mutating lookup: the result id for these ingredients, or null. */
  lookupRecipe: (ingredients: string[]) => string | null;
  resetUnlocked: () => void;
  saveToLocalStorage: () => void;
  loadFromLocalStorage: () => void;
}

export const useElementStore = create<ElementState>((set, get) => {
  const savedGame = loadGameState();
  const initialUnlocked = new Set<string>();
  Object.keys(baseElements).forEach((id) => initialUnlocked.add(id));
  if (savedGame.unlocked && savedGame.unlocked.length > 0) {
    savedGame.unlocked.forEach((id) => {
      if (ELEMENT_DEFS[id]) {
        initialUnlocked.add(id);
      }
    });
  }

  return {
    all: ELEMENT_DEFS,
    unlocked: initialUnlocked,
    selected: [],
    history: [],

    unlock: (id) =>
      set((state) => {
        if (state.unlocked.has(id)) return state;
        const newUnlocked = new Set(state.unlocked);
        newUnlocked.add(id);

        const newHistory = [...state.history];
        newHistory.push(Array.from(state.unlocked));

        setTimeout(() => get().saveToLocalStorage(), 0);
        setTimeout(() => {
          const profile = loadUserProfile();
          if (profile.firebaseId) {
            syncGameState(profile.firebaseId).catch(console.error);
          }
        }, 0);

        return { unlocked: newUnlocked, history: newHistory };
      }),

    undoLastAction: () =>
      set((state) => {
        const newHistory = [...state.history];
        if (newHistory.length === 0) return state;
        const previous = newHistory.pop()!;
        return { unlocked: new Set(previous), history: newHistory };
      }),

    select: (id, idx) =>
      set((state) => {
        const selected = [...state.selected];
        selected[idx] = id;
        return { selected };
      }),

    clearSelection: () => set({ selected: [] }),

    discoverRecipe: (ingredients) => {
      const filteredIngredients = ingredients.filter((id) => id);
      if (filteredIngredients.length < 2) return null;

      const recipeKey = [...filteredIngredients].sort().join(",");
      let resultId: string | undefined;

      if (filteredIngredients.length === 2) resultId = recipeMap.get(recipeKey);
      else if (filteredIngredients.length === 3) resultId = recipeMap3.get(recipeKey);
      else if (filteredIngredients.length === 4) resultId = recipeMap4.get(recipeKey);

      if (resultId) {
        set((state) => {
          const unlocked = new Set(state.unlocked);
          if (unlocked.has(resultId!)) return state;
          const newHistory = [...state.history];
          newHistory.push(Array.from(state.unlocked));
          unlocked.add(resultId!);
          setTimeout(() => get().saveToLocalStorage(), 0);
          return { unlocked, history: newHistory };
        });
        return resultId;
      }

      return null;
    },

    lookupRecipe: (ingredients) => {
      const filtered = ingredients.filter((id) => id);
      if (filtered.length < 2) return null;
      const key = [...filtered].sort().join(",");
      const map =
        filtered.length === 2 ? recipeMap : filtered.length === 3 ? recipeMap3 : recipeMap4;
      return map.get(key) ?? null;
    },

    resetUnlocked: () =>
      set(() => {
        const unlocked = new Set<string>();
        Object.keys(baseElements).forEach((id) => unlocked.add(id));
        setTimeout(() => get().saveToLocalStorage(), 0);
        return { unlocked, history: [] };
      }),

    saveToLocalStorage: () => {
      const { unlocked } = get();
      saveGameState({
        unlocked: Array.from(unlocked),
        lastPlayed: Date.now(),
      });
    },

    loadFromLocalStorage: () => {
      const saved = loadGameState();
      const unlocked = new Set<string>();
      Object.keys(baseElements).forEach((id) => unlocked.add(id));
      if (saved.unlocked && saved.unlocked.length > 0) {
        saved.unlocked.forEach((id) => {
          if (ELEMENT_DEFS[id]) {
            unlocked.add(id);
          }
        });
      }
      set({ unlocked, history: [] });
    },
  };
});
